import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Link,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { AuthScreenShell, AuthCard, AuthCardHeader } from './auth';
import { productBrand } from '../productBrand';
import { isLocalhost } from '../utils/isLocalhost';

const DEMO_PASSWORD_MASK = '••••••••••';

interface SignInProps {
    onSwitchToSignUp: () => void;
}

const SignIn: React.FC<SignInProps> = ({ onSwitchToSignUp }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const { signIn, signInWithDemoToken, isLoading } = useAuth();
    const [searchParams] = useSearchParams();
    const demoParam = searchParams.get('demo');
    /** Set while the form holds the prefilled demo credentials; editing a field drops it. */
    const [demoToken, setDemoToken] = useState<string | null>(null);
    const [demoCompanyName, setDemoCompanyName] = useState<string | null>(null);

    const demoPreviewQuery = useQuery({
        queryKey: ['auth', 'demo-login-preview', demoParam],
        queryFn: () => apiService.getDemoLoginPreview(demoParam!),
        enabled: Boolean(demoParam),
        retry: false,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (!demoPreviewQuery.data || !demoParam) return;
        setEmail(demoPreviewQuery.data.email);
        setPassword(DEMO_PASSWORD_MASK);
        setShowPassword(false);
        setDemoToken(demoParam);
        setDemoCompanyName(demoPreviewQuery.data.companyName);
        setError('');
    }, [demoPreviewQuery.data, demoParam]);

    useEffect(() => {
        if (!demoPreviewQuery.isError) return;
        setError('El enlace demo no es válido o fue desactivado');
    }, [demoPreviewQuery.isError]);

    const signInMutation = useMutation({
        mutationFn: async () => {
            if (demoToken) {
                await signInWithDemoToken(demoToken);
            } else {
                await signIn(email, password);
            }
        },
        onError: () => {
            setError('Email o contraseña incorrectos');
        },
    });

    const formBusy = isLoading || signInMutation.isPending;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Por favor completa todos los campos');
            return;
        }

        signInMutation.mutate();
    };

    return (
        <AuthScreenShell>
            <AuthCard>
                <AuthCardHeader
                    title="Bienvenid@ de vuelta"
                    subtitle={`Inicia sesión para acceder a ${productBrand.name}`}
                    titleSx={{ fontFamily: productBrand.fontFamily }}
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {demoToken && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {demoCompanyName
                            ? `Acceso demo para ${demoCompanyName}. `
                            : 'Acceso demo. '}
                        Tus credenciales ya están completas, solo inicia sesión.
                    </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Correo electrónico"
                        name="email"
                        autoComplete="email"
                        autoFocus
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (demoToken) {
                                setDemoToken(null);
                                setPassword('');
                            }
                        }}
                        disabled={formBusy}
                    />
                    <TextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Contraseña"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => {
                            if (demoToken) {
                                setDemoToken(null);
                                setPassword('');
                                return;
                            }
                            setPassword(e.target.value);
                        }}
                        disabled={formBusy}
                        InputProps={{
                            endAdornment: demoToken ? undefined : (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        disabled={formBusy}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                        <Link
                            component={RouterLink}
                            to="/auth/olvido-contrasena"
                            variant="body2"
                            sx={{ color: 'primary.main' }}
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>
                    </Box>
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2, mb: 2, py: 1.5 }}
                        disabled={formBusy}
                    >
                        {signInMutation.isPending ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Iniciar Sesión'
                        )}
                    </Button>

                    {isLocalhost() && (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                ¿No tienes una cuenta?{' '}
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={onSwitchToSignUp}
                                    sx={{ cursor: 'pointer', color: 'primary.main' }}
                                >
                                    Regístrate
                                </Link>
                            </Typography>
                        </Box>
                    )}
                </Box>
            </AuthCard>
        </AuthScreenShell>
    );
};

export default SignIn;
